import { useCallback, useEffect, useRef } from 'react'
import useFormMethodsWithLegacyFormSupport from './useFormMethodsWithLegacySupport'
import useT from '@hooks/useTranslation'
import * as utils from './utils'

import styles from './RangeSlider.module.css'

function RangeSlider({
	defaultValue = 0,
	forwardRef,
	injectLabel,
	max = 2000000,
	min = 0,
	name,
	progressStyle,
	step = 1000,
	t,
	type,
	unit,
	...props
}) {
	const methods = useFormMethodsWithLegacyFormSupport()
	const { register, setValue, unregister } = methods || {}

	const ref = forwardRef || useRef()
	const progressRef = useRef()

	const labelText = t && utils.addUnitText(useT(t), unit)

	const inputProps = { max, min, step }

	useEffect(() => {
		if (!register) return
		register(name)
		return () => unregister(name)
	}, [name, register, unregister])

	const handleProgress = useCallback(
		(value) => {
			const _max = max - min
			const _value = value - min
			const width = Math.floor((_value / _max) * 100)
			progressRef.current.style.width = `${width}%`
		},
		[max],
	)

	useEffect(() => handleProgress(defaultValue), [handleProgress])

	const onChange = useCallback(
		(e) => {
			const {
				target: { value },
			} = e
			if (setValue) setValue(name, value)
			if (props.onChange) props.onChange(e)
			handleProgress(value)
		},
		[handleProgress, name, props.onChange, setValue],
	)

	return (
		<div {...props}>
			{ injectLabel }
			{labelText && (
				<label className={styles.label} htmlFor={name}>
					{labelText}
				</label>
			)}
			<div className={styles['range-slider']} id={name}>
				<input
					defaultValue={defaultValue}
					onChange={onChange}
					ref={ref}
					type="range"
					{...inputProps}
				/>
				<div className={styles.progress}>
					<div ref={progressRef} style={progressStyle} />
				</div>
			</div>
		</div>
	)
}

export default RangeSlider